import Checkbox from "@/v2-ui/checkbox"
import Label from "@/v2-ui/label"
import cn from "@/v2-ui/utils/utils.cn"
import LicenseFeatureGroupDetailsHoverCard from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupDetailsHoverCard"
import LicenseFeatureGroupIncludedMark from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupIncludedMark"
import LicenseFeatureGroupPackageOnlyMark from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupPackageOnlyMark"
import LicenseFeatureGroupPresetMenu from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupPresetMenu"
import LicenseFeatureGroupReachIndicator from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupReachIndicator"
import LicenseFeatureGroupReachMark from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupReachMark"
import LicenseFeatureGroupTickCheckbox from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupTickCheckbox"
import { ROW_NOTE_CLASS } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.constants"
import {
  isPersonLens,
  useFeatureGroupLens,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.context"
import {
  describeIncluded,
  describeReach,
  reachStateOf,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.utils"
import { tickMeaningOf } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.placement.utils"
import type { CapabilityGroupState } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.capabilityGroup.utils"
import {
  isLockable,
  kindLabelOf,
  otherPackagesOf,
  packagesOf,
  type LicenseFeatureGroupCapability,
  type LicenseFeatureGroupCapabilityTree,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.capability.utils"

export type LicenseFeatureGroupCapabilityGroupHeaderProps = {
  moduleName: string
  featureGroupName: string
  capabilities: readonly LicenseFeatureGroupCapability[]
  tree: LicenseFeatureGroupCapabilityTree
  state: CapabilityGroupState
  /** Whether rows follow beneath: decides the border and the count. */
  hasRows: boolean
  onSetModuleGranted(granted: boolean): void
  onSameAs(packageName: string): void
}

// The module card's header: the whole-module grant as a tri-state checkbox
// (or the fixed tick when there is nothing to decide), the module's name
// with its hover card, the hint that says what kind of grant it is, the
// pending adds and removes, the marks, the count, and the preset menu in
// the package lens. Presentation only: the state arrives derived.
function LicenseFeatureGroupCapabilityGroupHeader(
  props: LicenseFeatureGroupCapabilityGroupHeaderProps,
) {
  const {
    moduleName,
    featureGroupName,
    capabilities,
    tree,
    state,
    hasRows,
    onSetModuleGranted,
    onSameAs,
  } = props
  const lens = useFeatureGroupLens()
  const {
    root,
    headerState,
    isHeaderChanged,
    isFixed,
    rootStatus,
    hint,
    added,
    removed,
    grantedCount,
    includedCount,
    coverage,
    headerFacts,
    ownGrantOf,
  } = state
  // Through the person lens the header counts what reaches that person
  // (the root's own answer when the module is gated as a whole with
  // nothing beneath it); otherwise what is included or granted.
  const reachedCount = isPersonLens(lens)
    ? capabilities.filter((capability) => lens.reaches(capability.Code)).length
    : null
  const shownCount =
    reachedCount ?? (lens.kind === "customer" ? includedCount : grantedCount)
  // A module gated as a whole with nothing beneath it IS its row, so its
  // checkbox carries what the tick means like any row's (a dash for a
  // placement, a tick for access). A header over rows keeps the tri-state
  // of how much of the module this package carries.
  const isOwnRow = lens.kind === "package" && !hasRows && !!root
  const rootTickMeaning = root
    ? tickMeaningOf({
        isPackageOnly: isLockable(root),
        isCarriedHere: ownGrantOf(root) !== null,
        packageNames: packagesOf(root),
      })
    : null

  return (
    <div
      className={cn(
        "flex items-center gap-2 border-l-2 border-l-transparent bg-muted/40 py-1.5 pl-2.5 pr-1.5",
        hasRows && "border-b",
        isHeaderChanged && "border-l-primary bg-primary/5",
      )}
    >
      <Label
        className={cn(
          "flex min-w-0 flex-1 items-center gap-2.5 font-normal",
          isFixed || isPersonLens(lens) ? "cursor-default" : "cursor-pointer",
        )}
      >
        {isPersonLens(lens) ? (
          <LicenseFeatureGroupReachIndicator
            state={
              root && !hasRows
                ? lens.reaches(root.Code)
                  ? "all"
                  : "none"
                : reachStateOf(reachedCount ?? 0, capabilities.length)
            }
            title={
              root && !hasRows
                ? describeReach({
                    reaches: lens.reaches(root.Code),
                    status: rootStatus,
                    userName: lens.userName,
                    customerName: lens.customerName,
                  })
                : `${reachedCount ?? 0} of ${capabilities.length} in ${moduleName} reach ${lens.userName}; the rows say which.`
            }
          />
        ) : isFixed && lens.kind === "customer" ? (
          <LicenseFeatureGroupIncludedMark
            title={
              rootStatus
                ? describeIncluded(rootStatus, lens.customerName)
                : `Included: every part of ${moduleName} reaches ${lens.customerName}'s users already.`
            }
          />
        ) : isOwnRow && root && rootTickMeaning ? (
          <LicenseFeatureGroupTickCheckbox
            meaning={rootTickMeaning}
            isCarriedHere={ownGrantOf(root) !== null}
            packageName={featureGroupName}
            rowName={moduleName}
            otherPackages={packagesOf(root).filter(
              (name) => name !== featureGroupName,
            )}
            ariaLabel={`Grant everything in ${moduleName}, now and in future releases`}
            onToggle={onSetModuleGranted}
          />
        ) : (
          <Checkbox
            checked={headerState}
            aria-label={
              lens.kind === "customer"
                ? `Add everything in ${moduleName} for ${lens.customerName}, now and in future releases`
                : `Grant everything in ${moduleName}, now and in future releases`
            }
            title={
              lens.kind === "package"
                ? `How much of ${moduleName} ${featureGroupName} carries. Ticking the header grants the whole module as a rule, future additions included; the rows beneath say what each tick means.`
                : undefined
            }
            onCheckedChange={(checked) => {
              onSetModuleGranted(checked === true)
            }}
          />
        )}
        <LicenseFeatureGroupDetailsHoverCard
          title={moduleName}
          code={root?.Code}
          description={root?.Description}
          facts={headerFacts}
          provenance={root}
          inside={
            root
              ? {
                  surfaceCode: root.Code,
                  containedFeatureCount: root.ContainedFeatureCount,
                }
              : undefined
          }
        >
          <span className="min-w-[5rem] flex-1 truncate text-xs font-medium">
            {moduleName}
          </span>
        </LicenseFeatureGroupDetailsHoverCard>
        {hint && (
          <span className={ROW_NOTE_CLASS} title={hint}>
            {hint}
          </span>
        )}
        {(added > 0 || removed > 0) && (
          <span className="text-[11px] tabular-nums text-primary">
            {added > 0 && `+${added}`}
            {added > 0 && removed > 0 && " "}
            {removed > 0 && `−${removed}`}
          </span>
        )}
        {root && lens.kind === "package" && isLockable(root) && (
          <LicenseFeatureGroupPackageOnlyMark />
        )}
        {root &&
          lens.kind === "package" &&
          otherPackagesOf(root, featureGroupName) && (
            <span
              className={ROW_NOTE_CLASS}
              title={`Also in ${otherPackagesOf(root, featureGroupName)}`}
            >
              also in {otherPackagesOf(root, featureGroupName)}
            </span>
          )}
        {root &&
          lens.kind === "customer" &&
          lens.reaches &&
          !lens.isReadOnly && (
            <LicenseFeatureGroupReachMark
              reaches={lens.reaches(root.Code)}
              userName={lens.userName}
              compact
            />
          )}
        {hasRows ? (
          <span className="text-xs tabular-nums text-muted-foreground">
            {shownCount}/{capabilities.length}
          </span>
        ) : (
          root &&
          lens.kind === "package" && (
            <span className={ROW_NOTE_CLASS}>{kindLabelOf(root, false)}</span>
          )
        )}
      </Label>
      {hasRows && lens.kind === "package" && (
        <LicenseFeatureGroupPresetMenu
          subject={moduleName}
          coverage={coverage}
          total={capabilities.length}
          onEverything={() => {
            onSetModuleGranted(true)
          }}
          onNothing={() => {
            onSetModuleGranted(false)
          }}
          onSameAs={onSameAs}
        />
      )}
    </div>
  )
}

export default LicenseFeatureGroupCapabilityGroupHeader
